import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import CountyModal from "@/components/CountyModal";
import InvoiceModal, { usd } from "@/components/InvoiceModal";
import InvoiceViewModal from "@/components/InvoiceViewModal";
import AssignParentsModal from "@/components/AssignParentsModal";
import SocialWorkerAdmin from "@/components/SocialWorkerAdmin";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Building2, Plus, FileText, Users, DollarSign, Wallet, AlertCircle, Send, CheckCircle2, Pencil, UserCog, Armchair,
} from "lucide-react";

function Stat({ icon: Icon, label, value, tone, bg }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
          <Icon className={`h-5 w-5 ${tone}`} />
        </div>
        <div>
          <p className="font-display text-2xl font-bold leading-none text-slate-900">{value}</p>
          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

const INV_STATUS = {
  draft: { bg: "bg-slate-100", text: "text-slate-600", label: "Draft" },
  sent: { bg: "bg-amber-50", text: "text-amber-700", label: "Sent" },
  paid: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Paid" },
};

export default function AdminBilling() {
  const [counties, setCounties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [countyModal, setCountyModal] = useState(false);
  const [editCounty, setEditCounty] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invoiceCounty, setInvoiceCounty] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [assignCounty, setAssignCounty] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const load = async () => {
    try {
      const [c, i, s] = await Promise.all([api.get("/counties"), api.get("/invoices"), api.get("/billing/stats")]);
      setCounties(c.data);
      setInvoices(i.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/invoices/${id}/status`, { status });
      toast.success(status === "paid" ? "Marked as paid" : status === "sent" ? "Invoice sent" : "Updated");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openView = (inv) => {
    setViewInvoice(inv);
    setViewOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header subtitle="Company Billing Portal" />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="animate-fade-up">
          <h1 className="font-display text-3xl font-bold text-slate-900">Billing &amp; Contracts</h1>
          <p className="mt-1 text-slate-500">Charge counties and organizations for platform use.</p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={Building2} label="Counties" value={stats.counties ?? 0} tone="text-[#1e3a8a]" bg="bg-blue-50" />
          <Stat icon={DollarSign} label="Total Invoiced" value={usd(stats.total_invoiced)} tone="text-[#0d9488]" bg="bg-teal-50" />
          <Stat icon={Wallet} label="Paid" value={usd(stats.paid)} tone="text-emerald-600" bg="bg-emerald-50" />
          <Stat icon={AlertCircle} label="Outstanding" value={usd(stats.outstanding)} tone="text-orange-500" bg="bg-orange-50" />
        </div>

        <Tabs defaultValue="counties" className="mt-8">
          <TabsList data-testid="billing-tabs">
            <TabsTrigger value="counties" data-testid="tab-counties">Counties</TabsTrigger>
            <TabsTrigger value="workers" data-testid="tab-workers">Social Workers</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices {invoices.length > 0 && `(${invoices.length})`}</TabsTrigger>
          </TabsList>

          {/* Counties */}
          <TabsContent value="counties" className="mt-4" data-testid="counties-panel">
            <div className="mb-4 flex justify-end">
              <Button data-testid="add-county-btn" onClick={() => { setEditCounty(null); setCountyModal(true); }} className="bg-[#1e3a8a] hover:bg-[#172554]">
                <Plus className="mr-2 h-4 w-4" /> Add County
              </Button>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              {counties.map((c) => (
                <div key={c.id} data-testid={`county-card-${c.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-lg font-semibold text-slate-900">{c.name}</h3>
                      <p className="text-sm text-slate-500">{c.contact_name} {c.contact_email && `· ${c.contact_email}`}</p>
                      {(c.contract_start || c.contract_end) && (
                        <p className="mt-1 text-xs text-slate-400">Contract: {c.contract_start || "—"} → {c.contract_end || "—"}</p>
                      )}
                    </div>
                    <button onClick={() => { setEditCounty(c); setCountyModal(true); }} data-testid={`edit-county-${c.id}`} className="text-slate-400 hover:text-[#1e3a8a]">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex items-end justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">Annual contract</p>
                      <p className="font-display text-2xl font-bold text-[#1e3a8a]">{usd(c.annual_contract_amount)}</p>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <p>{c.usage?.parents ?? 0} parents</p>
                      <p>{c.usage?.completed_visits ?? 0} visits · {c.usage?.reports ?? 0} reports</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid={`county-seats-${c.id}`}>
                    <Armchair className="h-4 w-4 text-[#0d9488]" />
                    <span className="font-medium text-slate-700">{c.seats?.seats_used ?? 0}/{c.seats?.seat_limit ?? 0} seats used</span>
                    <span className="ml-auto text-slate-500">{c.seats?.seats_available ?? 0} available</span>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`assign-parents-btn-${c.id}`} onClick={() => { setAssignCounty(c); setAssignOpen(true); }} className="flex-1">
                      <Users className="mr-1.5 h-4 w-4" /> Parents
                    </Button>
                    <Button size="sm" data-testid={`create-invoice-btn-${c.id}`} onClick={() => { setInvoiceCounty(c); setInvoiceModal(true); }} className="flex-1 bg-[#0d9488] hover:bg-[#115e59]">
                      <FileText className="mr-1.5 h-4 w-4" /> Create Invoice
                    </Button>
                  </div>
                </div>
              ))}
              {counties.length === 0 && (
                <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
                  <Building2 className="mx-auto h-10 w-10 text-slate-300" />
                  <p className="mt-3 font-medium text-slate-600">No counties yet</p>
                  <p className="text-sm text-slate-400">Add a county to start billing for platform use.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Social Workers */}
          <TabsContent value="workers" className="mt-4" data-testid="workers-panel">
            <SocialWorkerAdmin counties={counties} onChanged={load} />
          </TabsContent>

          {/* Invoices */}
          <TabsContent value="invoices" className="mt-4 space-y-3" data-testid="invoices-panel">
            {invoices.map((inv) => {
              const s = INV_STATUS[inv.status] || INV_STATUS.draft;
              return (
                <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                      <button onClick={() => openView(inv)} data-testid={`view-invoice-${inv.id}`} className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-slate-800">{inv.invoice_number}</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>
                        </div>
                        <p className="mt-0.5 text-sm text-slate-500">{inv.county_name} · {inv.period_label}</p>
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-lg font-bold text-slate-900">{usd(inv.total)}</span>
                      {inv.status === "draft" && (
                        <Button size="sm" variant="outline" data-testid={`send-invoice-${inv.id}`} onClick={() => setStatus(inv.id, "sent")}>
                          <Send className="mr-1.5 h-4 w-4" /> Mark Sent
                        </Button>
                      )}
                      {inv.status !== "paid" && (
                        <Button size="sm" data-testid={`pay-invoice-${inv.id}`} onClick={() => setStatus(inv.id, "paid")} className="bg-emerald-600 hover:bg-emerald-700">
                          <CheckCircle2 className="mr-1.5 h-4 w-4" /> Mark Paid
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {invoices.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <FileText className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 font-medium text-slate-600">No invoices yet</p>
                <p className="text-sm text-slate-400">Create an invoice from a county to bill for use.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <CountyModal open={countyModal} onOpenChange={setCountyModal} county={editCounty} onSaved={load} />
      <InvoiceModal open={invoiceModal} onOpenChange={setInvoiceModal} county={invoiceCounty} onCreated={load} />
      <InvoiceViewModal invoice={viewInvoice} open={viewOpen} onOpenChange={setViewOpen} />
      <AssignParentsModal open={assignOpen} onOpenChange={setAssignOpen} county={assignCounty} onSaved={load} />
    </div>
  );
}
